/**
 * Notifications Page
 * Comprehensive notification management with filtering, pagination, and actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import NotificationItem from '../components/NotificationItem';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  BellIcon, 
  BellSlashIcon, 
  FunnelIcon, 
  MagnifyingGlassIcon,
  CheckIcon,
  TrashIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { Alert, AlertDescription } from '../components/ui/alert';

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { 
    notifications, 
    unreadCount, 
    isLoading, 
    error, 
    refreshNotifications, 
    markAllAsRead, 
    markAsRead,
    deleteNotification 
  } = useNotifications();

  // Local state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterRead, setFilterRead] = useState<string>('all');
  const [pageLoading, setPageLoading] = useState(false);
  const [selectedNotifications, setSelectedNotifications] = useState<Set<string>>(new Set());

  // Load notifications when page mounts
  useEffect(() => {
    const loadInitialNotifications = async () => {
      setPageLoading(true);
      await refreshNotifications();
      setPageLoading(false);
    };
    loadInitialNotifications();
  }, [refreshNotifications]);

  // Filter notifications based on search and filters
  const filteredNotifications = notifications.filter(notification => {
    // Search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = 
        notification.title.toLowerCase().includes(searchLower) ||
        notification.message.toLowerCase().includes(searchLower) ||
        notification.type.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Type filter
    if (filterType !== 'all' && notification.type !== filterType) {
      return false;
    }

    // Read status filter
    if (filterRead === 'unread' && notification.read) {
      return false;
    }
    if (filterRead === 'read' && !notification.read) {
      return false;
    }

    return true;
  });

  // Handle navigation from notification clicks
  const handleNavigate = useCallback((link: string) => {
    navigate(link);
  }, [navigate]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setPageLoading(true);
    await refreshNotifications();
    setPageLoading(false);
  }, [refreshNotifications]);

  // Handle mark all as read
  const handleMarkAllAsRead = useCallback(async () => {
    await markAllAsRead();
    await refreshNotifications();
  }, [markAllAsRead, refreshNotifications]);

  // Handle bulk actions
  const handleBulkMarkAsRead = useCallback(async () => {
    const unreadSelected = Array.from(selectedNotifications).filter(id => {
      const notification = notifications.find(n => n.notification_id === id);
      return notification && !notification.read;
    });

    for (const id of unreadSelected) {
      await markAsRead(id);
    }
    
    setSelectedNotifications(new Set());
    await refreshNotifications();
  }, [selectedNotifications, notifications, markAsRead, refreshNotifications]);

  const handleBulkDelete = useCallback(async () => {
    for (const id of Array.from(selectedNotifications)) {
      await deleteNotification(id);
    }
    
    setSelectedNotifications(new Set());
    await refreshNotifications();
  }, [selectedNotifications, deleteNotification, refreshNotifications]);

  // Handle individual notification selection
  const handleNotificationSelect = useCallback((notificationId: string, selected: boolean) => {
    setSelectedNotifications(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(notificationId);
      } else {
        newSet.delete(notificationId);
      }
      return newSet;
    });
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setFilterType('all');
    setFilterRead('all');
  }, []);

  // Get notification type options
  const notificationTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'qbr_completed', label: 'QBR Reports' },
    { value: 'insight_generated', label: 'Insights' },
    { value: 'health_change', label: 'Health Changes' },
    { value: 'integration_status', label: 'Integrations' },
    { value: 'mapping_required', label: 'Mapping Required' },
    { value: 'team_invitation', label: 'Team Invitations' },
  ];

  const unreadFilteredCount = filteredNotifications.filter(n => !n.read).length;
  const hasActiveFilters = searchQuery || filterType !== 'all' || filterRead !== 'all';

  return (
    <div className="container mx-auto py-8 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <BellIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              Notifications
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {unreadCount > 0 ? `${unreadCount} unread notifications` : 'All caught up!'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {unreadCount > 0 && (
            <Button 
              onClick={handleMarkAllAsRead} 
              variant="outline" 
              size="sm"
              className="dark:text-blue-400 dark:hover:text-blue-300"
            >
              <CheckIcon className="h-4 w-4 mr-2" />
              Mark all as read
            </Button>
          )}
          <Button 
            onClick={handleRefresh} 
            variant="outline" 
            size="sm"
            disabled={pageLoading}
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${pageLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search notifications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Type Filter */}
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              {notificationTypes.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Read Status Filter */}
          <Select value={filterRead} onValueChange={setFilterRead}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="unread">Unread only</SelectItem>
              <SelectItem value="read">Read only</SelectItem>
            </SelectContent>
          </Select>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button 
              onClick={clearFilters} 
              variant="outline" 
              size="sm"
              className="self-end"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Clear
            </Button>
          )}
        </div>

        {/* Filter Summary */}
        {hasActiveFilters && (
          <div className="mt-3 flex items-center space-x-2">
            <span className="text-sm text-gray-500 dark:text-gray-400">Showing:</span>
            {searchQuery && (
              <Badge variant="secondary">Search: "{searchQuery}"</Badge>
            )}
            {filterType !== 'all' && (
              <Badge variant="secondary">
                Type: {notificationTypes.find(t => t.value === filterType)?.label}
              </Badge>
            )}
            {filterRead !== 'all' && (
              <Badge variant="secondary">
                Status: {filterRead === 'unread' ? 'Unread' : 'Read'}
              </Badge>
            )}
            <Badge variant="outline">
              {filteredNotifications.length} of {notifications.length} notifications
            </Badge>
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      {selectedNotifications.size > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <span className="text-sm text-blue-700 dark:text-blue-300">
              {selectedNotifications.size} notification{selectedNotifications.size !== 1 ? 's' : ''} selected
            </span>
            <div className="flex space-x-2">
              <Button 
                onClick={handleBulkMarkAsRead} 
                variant="outline" 
                size="sm"
                className="text-green-600 hover:text-green-700"
              >
                <CheckIcon className="h-4 w-4 mr-2" />
                Mark as read
              </Button>
              <Button 
                onClick={handleBulkDelete} 
                variant="outline" 
                size="sm"
                className="text-red-600 hover:text-red-700"
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {pageLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          {/* Results Summary */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {filteredNotifications.length} notification{filteredNotifications.length !== 1 ? 's' : ''}
              </span>
              {unreadFilteredCount > 0 && (
                <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  {unreadFilteredCount} unread
                </Badge>
              )}
            </div>
          </div>

          {/* Notifications List */}
          {filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
              <BellSlashIcon className="h-16 w-16 mb-4" />
              <p className="text-lg font-medium">No notifications found</p>
              <p className="text-sm mt-2">
                {hasActiveFilters 
                  ? 'Try adjusting your filters or search terms'
                  : 'You\'re all caught up! New notifications will appear here.'
                }
              </p>
              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="outline" className="mt-4">
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredNotifications.map((notification) => (
                <div key={notification.notification_id} className="relative">
                  {/* Selection checkbox */}
                  <div className="absolute top-4 left-4 z-10">
                    <input
                      type="checkbox"
                      checked={selectedNotifications.has(notification.notification_id)}
                      onChange={(e) => handleNotificationSelect(notification.notification_id, e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  
                  {/* Notification item with padding for checkbox */}
                  <div className="ml-8">
                    <NotificationItem 
                      notification={notification} 
                      onNavigate={handleNavigate}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NotificationsPage;
